import React from "react";
import moment from "moment";
import lastItem from "lodash/last";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Typography } from "@material-ui/core";
import Tick from "./Tick";
import { createRangeWithTooltip } from "./Slider";
import { sequence, formatDate } from "../../../common/utils";
const TooltipRange = createRangeWithTooltip();
const toTimestamp = (date) => parseInt(moment(date).format("x"), 10);
const fromTimestamp = (timestamp) => moment(timestamp).toISOString();

// useStyles is a hook for styling this component with Material UI's styling solution
const useStyles = makeStyles(() => ({
    wrapper: {
    width: "100%",
    boxSizing: "border-box",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "0 22px 24px",
  },

    labels: {
    marginTop: 4,
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
  },
  labelText: {
    whiteSpace: "nowrap",
  },

  container: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },

  label: {
    position: "absolute",
    top: 18,
    whiteSpace: "nowrap",
  },
}));

// main export
const TimePeriodSelector = ({
  start,
  end,
  dates = [],
  labels = 4,
  customColor = "",
  onChange = () => {},
}) => {
  // custom style
  const classes = useStyles();
  
  const timestamps = dates.map(toTimestamp).sort((a, b) => a - b);
  const interval = (lastItem(timestamps) - timestamps[0]) / (labels - 1);
  const marks = timestamps.reduce(
    (acc, current) => ({ ...acc, [current]: "" }),
    {}
  );

  const startVal = !start ? timestamps[0] : toTimestamp(start);
  const endVal = !end ? lastItem(timestamps) : toTimestamp(end);

  /**
   * @function nearestMark
   * @description The image date nearest a value (the earlier on a tie). With `step={null}` a thumb
   * can only sit on an image date; outside an interaction, a thumb on the date nearest its typed
   * date is taken as not moved.
   * @param {Number} value - Timestamp.
   * @returns {Number}
   */
  const nearestMark = (value) =>
    timestamps.reduce((best, t) => (Math.abs(t - value) < Math.abs(best - value) ? t : best), timestamps[0]);

  const restingRef = React.useRef(null);
  const focusingRef = React.useRef(false);

  /**
   * @function handleChange
   * @description Reports the period, keeping a typed date for any thumb the user did not move.
   * rc-slider hands back both thumbs on every change, each snapped to a mark, so dragging one used
   * to replace the other's typed date with its neighbouring image date - and a typed date outside
   * the slider's range was replaced as soon as it rendered.
   * A thumb counts as unmoved while it sits where it was when the interaction began; outside an
   * interaction, as when a typed date falls outside the range, the nearest mark stands in.
   * A change reported while a thumb takes focus is ignored. On focus - tabbing out of the end date
   * lands on the start thumb - rc-slider reads the date back from the thumb's centre on screen, and
   * with these 22 px thumbs that is the neighbouring mark: a start typed as 01/02/2021, drawn on
   * 18/02/2021, came back as 30/12/2020 without anything being dragged.
   * @param {Number[]} val - The two thumbs' timestamps.
   * @returns {void}
   */
  const handleChange = (val) => {
    if (focusingRef.current) return;
    const unmoved = (index, shown) =>
      restingRef.current ? val[index] === restingRef.current[index] : val[index] === nearestMark(shown);
    const keep = (index, shown, original) =>
      original && unmoved(index, shown) ? moment(original).toISOString() : fromTimestamp(val[index]);
    onChange(keep(0, startVal, start), keep(1, endVal, end));
  };

  return (
    <Box
      className={classes.wrapper}
      id="timeselector"
      onFocusCapture={() => {
        focusingRef.current = true;
        setTimeout(() => {
          focusingRef.current = false;
        }, 0);
      }}>
      <TooltipRange
        style={{ width: "100%", padding: "0", overflow: "visible" }}
        min={timestamps[0]}
        max={lastItem(timestamps)}
        marks={marks}
        step={null}
        tipFormatter={(val) => formatDate(val)}
        value={[startVal, endVal]}
        onBeforeChange={(val) => {
          restingRef.current = val;
        }}
        onChange={handleChange}
        onAfterChange={() => {
          restingRef.current = null;
        }}
      />
       <Box className={classes.labels} display="flex" alignItems="center" style={{ width: "100%" }}>
        {sequence(labels).map((i) => (
          <Box
            key={i}
            className={classes.container}
            display="flex"
            alignItems="center"
            flexDirection="column"
          >
            <Tick height={15} />
            <Typography className={classes.label} variant="body1">
              {formatDate(Math.round(timestamps[0] + interval * i))}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default TimePeriodSelector;
